import React, { useState, useEffect } from 'react';
import { Trash2, ArrowRight, User, Calendar, Tag, X, CheckSquare } from 'lucide-react';
import { useBrand } from '../contexts/BrandContext';
import { supabase } from '../lib/supabase';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import type { PipelineStage } from '../lib/crmService';

interface BulkActionsProps {
  selectedOpportunityIds: string[];
  onClearSelection: () => void;
  onActionComplete: () => void;
}

interface BrandUser {
  id: string;
  email: string;
  full_name: string | null;
}

export default function BulkActions({
  selectedOpportunityIds,
  onClearSelection,
  onActionComplete,
}: BulkActionsProps) {
  const { selectedBrand } = useBrand();
  const { success, error } = useToast();
  const { confirm, confirmState, closeConfirm } = useConfirm();
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [users, setUsers] = useState<BrandUser[]>([]);
  const [tags, setTags] = useState<Array<{ id: string; name: string; color: string }>>([]);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => {
    if (selectedBrand) {
      loadStages();
      loadUsers();
      loadTags();
    }
  }, [selectedBrand]);

  const loadStages = async () => {
    if (!selectedBrand) return;

    const { data, error } = await supabase
      .from('pipeline_stages')
      .select('*')
      .eq('brand_id', selectedBrand.id)
      .order('order_index');

    if (!error && data) {
      setStages(data);
    }
  };

  const loadUsers = async () => {
    if (!selectedBrand) return;

    const { data, error } = await supabase
      .from('user_brands')
      .select('user_id, users(id, email, full_name)')
      .eq('brand_id', selectedBrand.id);

    if (!error && data) {
      const brandUsers = data
        .filter((ub) => ub.users)
        .map((ub) => ({
          id: ub.users!.id,
          email: ub.users!.email,
          full_name: ub.users!.full_name,
        }));
      setUsers(brandUsers);
    }
  };

  const loadTags = async () => {
    if (!selectedBrand) return;

    const { data, error } = await supabase
      .from('opportunity_tags')
      .select('*')
      .eq('brand_id', selectedBrand.id)
      .order('name');

    if (!error && data) {
      setTags(data);
    }
  };

  const handleBulkMove = async () => {
    if (!selectedStageId) return;

    const { error: updateError } = await supabase
      .from('opportunities')
      .update({ pipeline_stage_id: selectedStageId })
      .in('id', selectedOpportunityIds);

    if (updateError) {
      error('Failed to move opportunities');
      return;
    }

    for (const oppId of selectedOpportunityIds) {
      await supabase.from('opportunity_activities').insert({
        opportunity_id: oppId,
        activity_type: 'stage_change',
        description: 'Stage changed via bulk action',
      });
    }

    success(`Moved ${selectedOpportunityIds.length} opportunities`);
    setShowMoveModal(false);
    onActionComplete();
  };

  const handleBulkAssign = async () => {
    if (!selectedUserId) return;

    const { error: updateError } = await supabase
      .from('opportunities')
      .update({ user_id: selectedUserId })
      .in('id', selectedOpportunityIds);

    if (updateError) {
      error('Failed to assign opportunities');
      return;
    }

    success(`Assigned ${selectedOpportunityIds.length} opportunities`);
    setShowAssignModal(false);
    onActionComplete();
  };

  const handleBulkUpdateDate = async () => {
    if (!selectedDate) return;

    const { error: updateError } = await supabase
      .from('opportunities')
      .update({ expected_close_date: selectedDate })
      .in('id', selectedOpportunityIds);

    if (updateError) {
      error('Failed to update close dates');
      return;
    }

    success(`Updated close date for ${selectedOpportunityIds.length} opportunities`);
    setShowDateModal(false);
    onActionComplete();
  };

  const handleBulkAddTags = async () => {
    if (selectedTags.length === 0) return;

    const assignments = selectedOpportunityIds.flatMap((oppId) =>
      selectedTags.map((tagId) => ({
        opportunity_id: oppId,
        tag_id: tagId,
      }))
    );

    const { error: insertError } = await supabase
      .from('opportunity_tag_assignments')
      .upsert(assignments, { onConflict: 'opportunity_id,tag_id', ignoreDuplicates: true });

    if (insertError) {
      error('Failed to add tags');
      return;
    }

    success(`Added tags to ${selectedOpportunityIds.length} opportunities`);
    setShowTagModal(false);
    setSelectedTags([]);
    onActionComplete();
  };

  const handleBulkDelete = async () => {
    const confirmed = await confirm(
      `Are you sure you want to delete ${selectedOpportunityIds.length} opportunities? This action cannot be undone.`
    );

    if (!confirmed) return;

    const { error: deleteError } = await supabase
      .from('opportunities')
      .delete()
      .in('id', selectedOpportunityIds);

    if (deleteError) {
      error('Failed to delete opportunities');
      return;
    }

    success(`Deleted ${selectedOpportunityIds.length} opportunities`);
    onActionComplete();
  };

  if (selectedOpportunityIds.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-blue-600 shadow-2xl z-40 transition-all">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-blue-600" />
              <span className="font-semibold text-gray-900">
                {selectedOpportunityIds.length} selected
              </span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowMoveModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
              >
                <ArrowRight className="w-4 h-4" />
                Move Stage
              </button>

              <button
                onClick={() => setShowAssignModal(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                Assign
              </button>

              <button
                onClick={() => setShowDateModal(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Set Date
              </button>

              <button
                onClick={() => setShowTagModal(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2"
              >
                <Tag className="w-4 h-4" />
                Add Tags
              </button>

              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg text-sm font-medium hover:bg-red-50 flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>

              <button
                onClick={onClearSelection}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {showMoveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Move to Stage</h3>
            <select
              value={selectedStageId}
              onChange={(e) => setSelectedStageId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            >
              <option value="">Select a stage...</option>
              {stages.map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowMoveModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkMove}
                disabled={!selectedStageId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Move {selectedOpportunityIds.length} Opportunities
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Assign to Team Member</h3>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            >
              <option value="">Select a team member...</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name || user.email}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowAssignModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAssign}
                disabled={!selectedUserId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Assign {selectedOpportunityIds.length} Opportunities
              </button>
            </div>
          </div>
        </div>
      )}

      {showDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Set Expected Close Date</h3>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDateModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpdateDate}
                disabled={!selectedDate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update {selectedOpportunityIds.length} Opportunities
              </button>
            </div>
          </div>
        </div>
      )}

      {showTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Add Tags</h3>
            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {tags.map((tag) => (
                <label
                  key={tag.id}
                  className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTags.includes(tag.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTags([...selectedTags, tag.id]);
                      } else {
                        setSelectedTags(selectedTags.filter((t) => t !== tag.id));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span
                    className="px-3 py-1 rounded-full text-sm font-medium text-white"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowTagModal(false);
                  setSelectedTags([]);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAddTags}
                disabled={selectedTags.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to {selectedOpportunityIds.length} Opportunities
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmState.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirm Action</h3>
            <p className="text-gray-700 mb-6">{confirmState.message}</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  confirmState.onCancel();
                  closeConfirm();
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmState.onConfirm();
                  closeConfirm();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
